import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Distance the pointer may travel before a press stops counting as a click.
 * Without this a launcher becomes un-openable: every click carries a pixel or
 * two of jitter, and treating that as a drag would swallow the tap.
 */
const CLICK_SLOP_PX = 4;
/** Keeps the box on screen, matching useFloatingBox's viewport margin. */
const EDGE_MARGIN_PX = 12;
/** Keyboard nudge per arrow press. */
const STEP_PX = 24;
/** Elements whose own click must never start a drag of their container. */
const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, [role="button"], [contenteditable="true"]';

const clamp = (value, min, max) =>
  Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Keep a box fully on screen.
 * @param {{left: number, top: number}} position
 * @param {number} width
 * @param {number} height
 * @returns {{left: number, top: number}}
 */
function clampToViewport(position, width, height) {
  return {
    left: clamp(
      position.left,
      EDGE_MARGIN_PX,
      window.innerWidth - width - EDGE_MARGIN_PX
    ),
    top: clamp(
      position.top,
      EDGE_MARGIN_PX,
      window.innerHeight - height - EDGE_MARGIN_PX
    ),
  };
}

/**
 * Read a persisted position, ignoring anything corrupt or out of range.
 * @param {string} key
 * @param {{left: number, top: number}} fallback
 * @returns {{left: number, top: number}}
 */
function loadPosition(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    const left = Number(parsed?.left);
    const top = Number(parsed?.top);
    if (!Number.isFinite(left) || !Number.isFinite(top)) return fallback;
    return { left, top };
  } catch {
    return fallback;
  }
}

/**
 * Drag-to-move for a fixed-position box, persisted per `storageKey`.
 *
 * For a fixed-size box such as a launcher. A panel, whose size changes as it is
 * resized, uses `useFloatingBox` instead — there position and size have to be
 * one piece of state, or resizing and re-clamping fight each other.
 *
 * Pointer capture (a fast drag must not detach from the handle), viewport
 * re-clamping on window resize, localStorage persistence, and arrow-key nudging
 * with `Home` to reset. `snapToEdge` makes the launcher tidy itself against the
 * nearer side on release.
 *
 * @param {Object} opts
 * @param {string} opts.storageKey
 * @param {number} opts.width - Current box width, for clamping.
 * @param {number} opts.height - Current box height, for clamping.
 * @param {{left: number, top: number}} opts.defaultPosition
 * @param {boolean} [opts.snapToEdge=false] - Park against the nearer vertical
 *   edge on release. Right for a launcher, wrong for a panel.
 * @param {boolean} [opts.ignoreInteractiveTargets=false] - Skip drags that
 *   start on a button/input inside the handle. Required when the handle is a
 *   whole header containing its own controls; must stay off when the handle IS
 *   the button, or it would block every drag.
 * @param {boolean} [opts.disabled=false] - Skips all gesture wiring.
 * @returns {{style: Object, isDragging: boolean, handleProps: Object,
 *   consumeDragged: () => boolean}}
 */
export function useDragPosition({
  storageKey,
  width,
  height,
  defaultPosition,
  snapToEdge = false,
  ignoreInteractiveTargets = false,
  disabled = false,
}) {
  const [position, setPosition] = useState(() =>
    loadPosition(storageKey, defaultPosition)
  );
  const [isDragging, setIsDragging] = useState(false);
  // Live position during a gesture. Null when idle, so the box renders from the
  // persisted position instead.
  const [drag, setDrag] = useState(null);
  // Where the gesture started, so the delta is measured from the grab point.
  const originRef = useRef(null);
  // Read by the click handler, which fires after pointerup.
  const movedRef = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(position));
    } catch {
      // Persistence is a nicety; a full or blocked store must not break drag.
    }
  }, [storageKey, position]);

  // A box placed for a big window must not sit off a small one — and the panel
  // changes size as it is resized, which moves its bottom-right corner too.
  useEffect(() => {
    if (disabled) return undefined;
    const reclamp = () =>
      setPosition((prev) => {
        const next = clampToViewport(prev, width, height);
        return next.left === prev.left && next.top === prev.top ? prev : next;
      });
    reclamp();
    window.addEventListener("resize", reclamp);
    return () => window.removeEventListener("resize", reclamp);
  }, [disabled, width, height]);

  const nudge = useCallback(
    (deltaLeft, deltaTop) => {
      setPosition((prev) =>
        clampToViewport(
          { left: prev.left + deltaLeft, top: prev.top + deltaTop },
          width,
          height
        )
      );
    },
    [width, height]
  );

  const endDrag = useCallback(
    (event) => {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      const origin = originRef.current;
      originRef.current = null;
      setIsDragging(false);
      setDrag(null);
      if (!origin || !movedRef.current) return;

      const moved = clampToViewport(
        {
          left: origin.left + (event.clientX - origin.x),
          top: origin.top + (event.clientY - origin.y),
        },
        width,
        height
      );

      if (!snapToEdge) {
        setPosition(moved);
        return;
      }
      // Park against whichever vertical edge the box ended up nearer.
      const center = moved.left + width / 2;
      setPosition({
        ...moved,
        left:
          center < window.innerWidth / 2
            ? EDGE_MARGIN_PX
            : window.innerWidth - width - EDGE_MARGIN_PX,
      });
    },
    [width, height, snapToEdge]
  );

  const handleProps = disabled
    ? {}
    : {
        onPointerDown: (event) => {
          // Ignore secondary buttons so a right-click never starts a drag.
          if (event.button !== 0) return;
          // A press that lands on a control inside the handle belongs to that
          // control, not to the drag.
          if (
            ignoreInteractiveTargets &&
            event.target?.closest?.(INTERACTIVE_SELECTOR)
          ) {
            return;
          }
          event.currentTarget.setPointerCapture?.(event.pointerId);
          originRef.current = {
            x: event.clientX,
            y: event.clientY,
            left: position.left,
            top: position.top,
          };
          movedRef.current = false;
          setIsDragging(true);
        },
        onPointerMove: (event) => {
          const origin = originRef.current;
          if (!origin) return;
          const dx = event.clientX - origin.x;
          const dy = event.clientY - origin.y;
          if (!movedRef.current && Math.hypot(dx, dy) > CLICK_SLOP_PX) {
            movedRef.current = true;
          }
          if (!movedRef.current) return;
          setDrag(
            clampToViewport(
              { left: origin.left + dx, top: origin.top + dy },
              width,
              height
            )
          );
        },
        onPointerUp: endDrag,
        onPointerCancel: endDrag,
        onKeyDown: (event) => {
          if (event.key === "ArrowUp") nudge(0, -STEP_PX);
          else if (event.key === "ArrowDown") nudge(0, STEP_PX);
          else if (event.key === "ArrowLeft") nudge(-STEP_PX, 0);
          else if (event.key === "ArrowRight") nudge(STEP_PX, 0);
          else if (event.key === "Home") setPosition(defaultPosition);
          else return;
          event.preventDefault();
        },
      };

  const live = drag ?? position;

  return {
    style: { left: `${live.left}px`, top: `${live.top}px` },
    isDragging,
    handleProps,
    /**
     * True when the press that just ended travelled far enough to count as a
     * drag rather than a click — and clears the flag as it reports it.
     *
     * Consuming rather than just reading matters: a drag still fires a `click`
     * on the captured element, so the click handler is where the flag must be
     * spent. Leaving it set would swallow the NEXT activation, including a
     * keyboard Enter that never produced a pointer gesture at all.
     */
    consumeDragged: () => {
      const dragged = movedRef.current;
      movedRef.current = false;
      return dragged;
    },
  };
}

export default useDragPosition;
